import { useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper
} from '@mui/material';
import AttributeEditor from './AttributeEditor';
import StructuredDataTree from './StructuredDataTree';
import BoundJsonAttribute from './BoundJsonAttribute';

/**
 * CodexAttributesView - Component for displaying and organizing codex attributes
 * 
 * @param {object} props
 * @param {object} props.data - Object containing all attribute key-value pairs
 * @param {string} props.codexId - The codex ID
 * @param {boolean} props.isEditing - Whether in editing mode
 * @param {function} props.onUpdate - Function called when data is updated
 * @param {function} props.onJsonError - Function called when JSON errors occur
 */
const CodexAttributesView = ({ data, codexId, isEditing, onUpdate, onJsonError, attributeDefs = {}, validations = {} }) => {
  // Stable handlers to prevent unnecessary rerenders - MUST BE DEFINED BEFORE ANY RETURNS
  const handleUpdate = useCallback((newData) => onUpdate(newData), [onUpdate]);
  const handleError = useCallback((field, hasError) => onJsonError(field, hasError), [onJsonError]);
  
  // Handle empty data case
  if (!data || Object.keys(data).length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No attributes available</Typography>
      </Box>
    );
  }

  // Split attributes into primitive and structured types
  const primitiveAttributes = {};
  const structuredAttributes = {};

  Object.entries(data).forEach(([key, value]) => {
    if (value && typeof value === 'object') {
      structuredAttributes[key] = value;
    } else {
      primitiveAttributes[key] = value;
    }
  });

  // Render a section of attributes with a title
  const renderAttributeSection = (attributes, title) => {
    if (Object.keys(attributes).length === 0) return null;

    return (
      <Box sx={{ mb: 3 }}>
        {title && (
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {title}
          </Typography>
        )}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ '& td': { borderBottom: 'none', py: 1, px: 1.5 } }}>
            <TableBody>
              {Object.entries(attributes).map(([attribute, value]) => (
                <TableRow key={attribute}>
                  <TableCell sx={{ width: '40%', fontWeight: 600, color: 'text.secondary' }}>
                    {attribute}
                  </TableCell>
                  <TableCell sx={{ width: '60%' }}>
                    {(() => {
                      const def = attributeDefs[attribute];
                      const registryLookup = def?.dataType === 'REGISTRY_LOOKUP'
                        ? def?.schema?.registryLookup : undefined;
                      return (
                        <AttributeEditor
                          attribute={attribute}
                          value={value}
                          codexId={codexId}
                          isEditing={isEditing}
                          onChange={(attr, val) => handleUpdate({ ...data, [attr]: val })}
                          onJsonError={handleError}
                          registryLookup={registryLookup}
                          validation={validations[attribute]}
                        />
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box>
      {/* Regular attributes */}
      {renderAttributeSection(primitiveAttributes, "Informazioni principali")}

      {/* Structured attributes */}
      {Object.entries(structuredAttributes).map(([attribute, value]) => {
        const def = attributeDefs[attribute];
        const bindings = def?.dataType === 'JSON' && !Array.isArray(value)
          ? def?.schema?.registryBindings : undefined;
        const isBound = Array.isArray(bindings) && bindings.length > 0;
        return (
          <Box key={attribute} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {attribute}
            </Typography>
            {isBound ? (
              <BoundJsonAttribute
                value={value}
                bindings={bindings}
                validation={validations[attribute]}
                isEditing={isEditing}
                onChange={(nextValue) => handleUpdate({ ...data, [attribute]: nextValue })}
                onJsonError={handleError}
              />
            ) : (
              <StructuredDataTree
                data={value}
                editable={isEditing}
                onChange={isEditing ? (nextValue) => handleUpdate({ ...data, [attribute]: nextValue }) : undefined}
                showJsonToggle={isEditing}
                onJsonError={handleError}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default CodexAttributesView; 
